// Loads firm-wide settings from /api/data/settings on app boot, runs a
// one-time migration of any legacy localStorage blobs, and exposes both
// a React hook (for components that want re-renders) and a sync helper
// surface (for legacy code that needs to read at module-eval time).
//
// Most components will use the sync getFirmSetting() / setFirmSetting()
// helpers from utils/firmSettings.js — this provider just keeps the
// underlying cache hot.

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { setFirmSettingsCache, subscribeFirmSettings, getFirmSettingsCache } from '../utils/firmSettingsStore';

const FirmSettingsContext = createContext(null);

// Legacy localStorage key → firm_settings JSONB key.
// Migration runs once: if the firm_settings entry is missing for a key
// AND localStorage has a value, push it up.
const MIGRATION_MAP = {
  'bcc_pl_edits':                    { key: 'plOverridesGlobal',     parse: 'json' },
  'bcc_class_overrides':             { key: 'plOverridesClasses',    parse: 'json' },
  'bcc_forecast_scenario':           { key: 'forecastScenario',      parse: 'string' },
  'bcc_scenarios':                   { key: 'scenarios',             parse: 'json' },
  'bcc_scenario_floor':              { key: 'scenarioFloor',         parse: 'number' },
  'bcc_cf_overrides':                { key: 'cfOverrides',           parse: 'json' },
  'bcc_cf_defaults':                 { key: 'cfDefaults',            parse: 'json' },
  'bcc_pipeline_coverage_targets':   { key: 'pipelineCoverageTargets', parse: 'json' },
};

const MIGRATED_FLAG = 'bcc_firm_settings_migrated_at';

async function fetchSettings() {
  const res = await apiFetch('/api/data/settings');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch firm settings');
  return data.settings || {};
}

async function patchSettings(keys) {
  const res = await apiFetch('/api/data/settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keys }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to patch firm settings');
  return data.settings || {};
}

function readLegacyValue(lsKey, parser) {
  const raw = localStorage.getItem(lsKey);
  if (raw == null) return undefined;
  if (parser === 'json') {
    try { return JSON.parse(raw); } catch { return undefined; }
  }
  if (parser === 'number') {
    const n = parseFloat(raw);
    return isNaN(n) ? undefined : n;
  }
  return raw; // string
}

export function FirmSettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [migrated, setMigrated] = useState({}); // { keyName: true } per migrated entry
  const initialized = useRef(false);

  // Initial load + one-time migration
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        let live = await fetchSettings();

        // Build migration patch for any legacy keys not yet in the DB
        const migrationPatch = {};
        const migratedReport = {};
        for (const [lsKey, { key, parse }] of Object.entries(MIGRATION_MAP)) {
          if (live[key] !== undefined) continue; // already in DB
          const value = readLegacyValue(lsKey, parse);
          if (value !== undefined && value !== null) {
            migrationPatch[key] = value;
            migratedReport[key] = true;
          }
        }
        if (Object.keys(migrationPatch).length > 0) {
          console.info('[FirmSettings] Migrating', Object.keys(migrationPatch), 'from localStorage to Supabase');
          live = await patchSettings(migrationPatch);
          localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
          setMigrated(migratedReport);
        }

        setSettings(live);
        setFirmSettingsCache(live);
        setLoading(false);
      } catch (e) {
        console.error('[FirmSettings] load failed:', e);
        setLoading(false);
      }
    })();
  }, []);

  // Sync React state with cache so consumers using useFirmSettings re-render
  useEffect(() => {
    const off = subscribeFirmSettings(s => setSettings(s));
    return off;
  }, []);

  const value = {
    settings,
    loading,
    migrated,
    migratedCount: Object.keys(migrated).length,
  };

  return <FirmSettingsContext.Provider value={value}>{children}</FirmSettingsContext.Provider>;
}

export function useFirmSettings() {
  const ctx = useContext(FirmSettingsContext);
  if (!ctx) throw new Error('useFirmSettings must be used within FirmSettingsProvider');
  return ctx;
}

// Convenience hook for a single key. Causes a re-render when that key changes.
export function useFirmSetting(key, defaultValue) {
  const { settings } = useFirmSettings();
  return settings[key] !== undefined ? settings[key] : defaultValue;
}
